import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Row, Col, Statistic, Table } from 'antd';
import { ContractorPaymentsApi } from 'api/api';
import { ContractorPaymentsSummaryDto } from 'api/models';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/page-headers/page-headers';
import { Main } from 'container/styled';
import { Cards } from 'components/cards/frame/cards-frame';
import dayjs from 'dayjs';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const contractorPaymentsApi = new ContractorPaymentsApi();

export const ContractorPaymentsSummaryPage = () => {
  const { contractorId } = useParams<{ contractorId: string }>();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ContractorPaymentsSummaryDto | null>(null);

  const fetchSummary = async () => {
    if (!contractorId) return;
    setLoading(true);
    try {
      const response = await contractorPaymentsApi.apiContractorsContractorIdPaymentsSummaryGet({
        contractorId: parseInt(contractorId),
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching payment summary:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSummary();
  }, [contractorId]);

  const projectColumns = [
    {
      title: t('payments:project', 'Project'),
      dataIndex: 'projectName',
      key: 'projectName',
    },
    {
      title: t('payments:total-amount', 'Total Amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount),
    },
    {
      title: t('payments:paid-amount', 'Paid Amount'),
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (amount: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount),
    },
    {
      title: t('payments:pending-amount', 'Pending Amount'),
      dataIndex: 'pendingAmount',
      key: 'pendingAmount',
      render: (amount: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount),
    },
    {
      title: t('payments:overdue-count', 'Overdue Count'),
      dataIndex: 'overdueCount',
      key: 'overdueCount',
    },
    {
      title: t('payments:next-due-date', 'Next Due Date'),
      dataIndex: 'nextDueDate',
      key: 'nextDueDate',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '-'),
    },
  ];

  const monthlyData = summary?.monthlyPayments
    ? Object.entries(summary.monthlyPayments).map(([key, value]) => ({
        month: key,
        amount: value,
        type: 'Paid',
      }))
    : [];

  if (summary?.monthlyPending) {
    Object.entries(summary.monthlyPending).forEach(([key, value]) => {
      monthlyData.push({
        month: key,
        amount: value,
        type: 'Pending',
      });
    });
  }

  const chartData = {
    labels: [...new Set(monthlyData.map(item => item.month))].sort(),
    datasets: [
      {
        label: 'Paid',
        data: monthlyData
          .filter(item => item.type === 'Paid')
          .sort((a, b) => a.month.localeCompare(b.month))
          .map(item => item.amount),
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
      },
      {
        label: 'Pending',
        data: monthlyData
          .filter(item => item.type === 'Pending')
          .sort((a, b) => a.month.localeCompare(b.month))
          .map(item => item.amount),
        borderColor: 'rgb(255, 99, 132)',
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    scales: {
      y: {
        ticks: {
          callback: function(value: string | number) {
            return `€${value}`;
          },
        },
      },
    },
  };

  return (
    <>
      <PageHeader ghost title={t('payments:payment-summary', 'Payment Summary')} />
      <Main>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Card loading={loading}>
              <Statistic
                title={t('payments:total-paid', 'Total Paid')}
                value={summary?.totalPaid}
                prefix="€"
                precision={2}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card loading={loading}>
              <Statistic
                title={t('payments:total-pending', 'Total Pending')}
                value={summary?.totalPending}
                prefix="€"
                precision={2}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card loading={loading}>
              <Statistic
                title={t('payments:overdue-amount', 'Overdue Amount')}
                value={summary?.overdueAmount}
                prefix="€"
                precision={2}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card loading={loading}>
              <Statistic title={t('payments:overdue-count', 'Overdue Count')} value={summary?.overdueCount} />
            </Card>
          </Col>
        </Row>

        <Card title={t('payments:monthly-trend', 'Monthly Payment Trend')} style={{ marginTop: 16 }} loading={loading}>
          <Line data={chartData} options={chartOptions} />
        </Card>

        <Card title={t('payments:project-summary', 'Project Summary')} style={{ marginTop: 16 }} loading={loading}>
          <Table
            loading={loading}
            dataSource={summary?.projectPayments || []}
            columns={projectColumns}
            rowKey="projectId"
          />
        </Card>
      </Main>
    </>
  );
};
